import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import LaunchBanner from "@/components/LaunchBanner";
import { HardHat, Clock, ArrowRight, Wrench, CreditCard, CheckCircle2, CloudRain, Lock, FileText, ShieldAlert } from "lucide-react";

const TRADES = ["Carpenter","Electrician","Plumber","Labourer","Plasterer","Roofer","Painter","Bricklayer","Scaffolder","Tiler","HVAC","Concreter","Welder","Landscaper","Gardener","Cleaner","Builder"];

const BENEFITS = [
  [HardHat, "Qualified, insured, rated", "Every tradie shows their qualification, insurance certificate, reliability score and real reviews before you book. Tradies post their free days, so you see who's available right now."],
  [CloudRain, "Rain week? Lend your crew", "Builders list their own workers when the weather shuts a site down — they earn with another builder instead of sitting at home."],
  [CreditCard, "Pay by credit card", "Visa, Mastercard or Amex through Stripe. Keep the points, no bank transfers, no chasing invoices."],
  [Lock, "Money safely held by Stripe until you sign off", "Payment sits with Stripe and releases only when the day's work is signed off. No-show? Full refund."],
  [ShieldAlert, "Problem on site? You're covered", "Flag an issue and the money stays locked until it's sorted — released to the tradie if the work's done, or straight back to you if it isn't."],
  [FileText, "Tax summaries in one tap", "Annual and BAS-quarter statements for every job, ready to hand to your accountant — tradies and builders."],
];

const STEPS = [
  { n: "01", t: "Post or list", d: "Builders post the job. Sole traders post today/tomorrow availability. Trade, location, day rate." },
  { n: "02", t: "Match & alert", d: "SMS pings matching tradies within seconds. First to accept locks it in." },
  { n: "03", t: "Book & pay", d: "Secure Stripe payment at booking, held until sign-off. Tradie gets paid same week." },
];

const INK = "text-[#121212]";

export default function Landing() {
  const { user, loading } = useAuth();
  useSEO({ path: "/", title: "TradieLoop — book qualified tradies, pay by card, held by Stripe", description: "TradieLoop (Tradie Loop) is Australia's marketplace for builders to find qualified, insured, rated tradies. Post a job or book who's free, pay by credit card with the money held by Stripe until sign-off, lend your crew on rain weeks, and get BAS-ready statements. Launching in Sydney.",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "Organization", "@id": `${window.location.origin}/#org`, name: "TradieLoop", alternateName: ["Tradie Loop", "TradieLoop Australia", "TradieLoop Aus"], url: window.location.origin, logo: `${window.location.origin}/tradieloop-logo.webp`, email: "info@tradieloopaus.com.au", areaServed: [{ "@type": "City", name: "Sydney" }, { "@type": "Country", name: "Australia" }], foundingLocation: { "@type": "City", name: "Sydney" }, description: "TradieLoop is an Australian marketplace connecting builders with qualified, insured tradies — book, pay by card via Stripe escrow, lend your crew on rain weeks. Not affiliated with Tradeloop." },
      { "@type": "WebSite", "@id": `${window.location.origin}/#website`, name: "TradieLoop", alternateName: "Tradie Loop", url: window.location.origin, publisher: { "@id": `${window.location.origin}/#org` }, potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/contractors?city={search_term_string}`, "query-input": "required name=search_term_string" } },
    ] } });
  const [stats, setStats] = useState({ tradies_online: null, available_today: [] });
  useEffect(() => {
    api.get("/meta/live-stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className={`min-h-screen bg-[#F6F4EF] ${INK}`} data-testid="landing-page">
      <LaunchBanner />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#D9D4C7]" data-testid="landing-hero">
        <picture className="absolute inset-0 pointer-events-none">
          <source media="(min-width: 768px)" srcSet="/hero-desktop.webp" type="image/webp" />
          <img src="/hero-mobile.webp" alt="" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover object-center" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-[#F6F4EF] via-[#F6F4EF]/95 to-[#F6F4EF]/55 md:to-[#F6F4EF]/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F6F4EF] to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-16 md:pt-20 md:pb-24 md:min-h-[calc(100vh-7.5rem)] md:flex md:flex-col md:justify-center">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 border border-[#D9D4C7] bg-[#F6F4EF] px-3 py-1.5 text-[11px] sm:text-xs uppercase tracking-widest font-bold font-mono-data" data-testid="landing-badge">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#16A34A] opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#16A34A]" /></span>
                Live now · Australia's tradie marketplace
              </div>
              <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] tracking-tighter">
                Need a hand <br />on-site?
              </h1>
              <div className="mt-5 h-2 w-24 bg-[#FF6A00]" aria-hidden />
              <p className="mt-6 text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-2xl">
                <span className="font-semibold text-[#121212]" data-testid="landing-brand-strap">TradieLoop</span> is where Australian builders find qualified tradies and keep them working. Post a job or book someone free this week, pay on your <span className="font-semibold text-[#121212]">credit card</span> with the money held by Stripe until you sign off, and if the rain sets in, <span className="font-semibold text-[#121212]">list your own crew</span> so they earn instead of sitting idle.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register?role=builder" className="group inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF6A00] text-black font-black uppercase text-sm tracking-widest hover:bg-[#121212] hover:text-white transition-colors duration-150" data-testid="cta-need-workers">
                  Find workers <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/register?role=contractor" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-[#121212] text-[#121212] font-black uppercase text-sm tracking-widest hover:bg-[#121212] hover:text-white transition-colors duration-150" data-testid="cta-need-work">
                  Find work <Wrench className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="relative border border-[#D9D4C7] bg-white/90 backdrop-blur-sm overflow-hidden shadow-xl shadow-zinc-900/10">
                <div className="grid grid-cols-2 divide-x divide-zinc-200 text-[#121212]">
                  {[
                    { k: "AVG. TIME TO FILL", v: "3h 42m", icon: Clock },
                    { k: "TRADIES ONLINE", v: stats.tradies_online == null ? "—" : stats.tradies_online.toLocaleString("en-AU"), icon: HardHat },
                  ].map((s) => (
                    <div key={s.k} className="p-4" data-testid={`stat-${s.k}`}>
                      <div className="flex items-center gap-1.5 text-zinc-400 text-[10px] font-semibold uppercase tracking-widest"><s.icon className="w-3.5 h-3.5" /> {s.k}</div>
                      <div className="mt-1.5 font-mono-data text-2xl font-semibold">{s.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why TradieLoop */}
      <section className="bg-white border-b border-[#D9D4C7]" data-testid="landing-benefits">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
          <div className="font-mono-data text-xs tracking-widest text-zinc-400">// WHY BUILDERS USE IT</div>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter">More than a job board.</h2>
          <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map(([Icon, t, d]) => (
              <li key={t} className="border border-[#D9D4C7] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-2 font-bold leading-snug"><Icon className="w-4 h-4 text-[#FF6A00] shrink-0 mt-0.5" /> {t}</div>
                <p className="mt-1.5 text-sm text-zinc-300 leading-relaxed">{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pay by card */}
      <section className="border-b border-[#D9D4C7]" data-testid="landing-card-promo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="font-mono-data text-xs tracking-widest text-zinc-400 flex items-center gap-2"><CreditCard className="w-4 h-4" /> PAY BY CARD · POWERED BY STRIPE</div>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter leading-[0.95]">
              Book a tradie on your<br />credit card. Earn the points.
            </h2>
            <p className="mt-4 text-zinc-300 text-base sm:text-lg leading-relaxed max-w-xl">Every booking runs through Stripe, so you can pay with Visa, Mastercard or Amex — no bank transfers, no chasing invoices. Your money is held by Stripe until the day's work is signed off.</p>
            <ul className="mt-6 grid sm:grid-cols-3 gap-3 text-sm max-w-3xl">
              {[["Points & cashback", "Keep earning on every job you book"], ["Held by Stripe", "Released only after end-of-day sign-off"], ["Full refund on no-show", "Money straight back if they don't turn up"]].map(([t, d]) => (
                <li key={t} className="border border-[#D9D4C7] bg-[#F6F4EF] p-3"><div className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" /> {t}</div><div className="text-zinc-300 text-xs mt-0.5">{d}</div></li>
              ))}
            </ul>
            <Link to="/register?role=builder" className="mt-7 inline-flex items-center gap-2 bg-[#FF6A00] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#121212] hover:text-white transition-colors" data-testid="card-promo-cta">Find a tradie <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-b border-[#D9D4C7]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-24">
          <div className="flex items-baseline justify-between mb-10">
            <h2 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight">How it runs</h2>
            <span className="hidden sm:block font-mono-data text-xs text-zinc-400 tracking-widest uppercase">// 03 STEPS</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-[#D9D4C7] bg-white p-6 shadow-sm" data-testid={`how-${s.n}`}>
                <div className="font-mono-data text-6xl font-semibold text-[#121212]">{s.n}</div>
                <div className="mt-1 h-1 w-10 bg-[#FF6A00]" aria-hidden />
                <div className="mt-4 font-display font-black text-xl uppercase">{s.t}</div>
                <p className="mt-2 text-zinc-300 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trades */}
      <section className="border-b border-[#D9D4C7]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <div className="flex items-baseline gap-4 mb-8">
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">Every trade on the tools</h2>
            <span className="font-mono-data text-xs text-zinc-400">// {TRADES.length} categories</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="landing-trades">
            {TRADES.map((t) => (
              <span key={t} className="px-3 py-2 border border-[#D9D4C7] bg-[#F6F4EF] text-sm font-medium">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="dark-zone bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tighter leading-none">
              Site starts at 7am. <br />Crew short?
            </h3>
            <p className="mt-3 text-zinc-300 font-semibold">Post the job in 30 seconds. Someone's already available.</p>
          </div>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6A00] text-black font-black uppercase text-sm tracking-widest hover:bg-white transition-colors" data-testid="cta-final-join">
            Start now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="dark-zone border-t border-white/10 bg-black text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 flex items-center justify-between text-xs font-mono-data text-zinc-500">
          <div className="flex items-center gap-3">
            <img src="/tradieloop-logo.webp" alt="TradieLoop" className="h-7 w-auto" />
            <span>© {new Date().getFullYear()} // Australia</span>
          </div>
          <span>BUILT FOR THE TOOLS</span>
        </div>
      </footer>
    </div>
  );
}
